import React, { useState, useEffect, useRef } from "react";
import { FORM_WIZARD_STEPS } from "./borrowerUploadConfig.js";

function ReviewGroup({ title, stepIndex, onEdit, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h4 className="text-sm font-bold text-slate-900">{title}</h4>
        <button
          type="button"
          onClick={() => onEdit(stepIndex)}
          className="text-xs font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2"
        >
          Edit
        </button>
      </div>
      <div className="space-y-2 text-sm text-slate-700">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-2 min-w-0">
      <span className="text-slate-500 shrink-0 sm:w-36">{label}</span>
      <span className="font-medium break-words">{String(value)}</span>
    </div>
  );
}

export default function FormReviewPanel({
  sections = [],
  uploads = {},
  uploadLabels = {},
  onEditStep,
  onSubmit,
  isSubmitting = false,
  declarations,
  hideSubmitButton = false,
  onFooterStateChange,
  documentsStepIndex = 2,
}) {
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const canSubmit = agree1 && agree2 && !isSubmitting;
  const onSubmitRef = useRef(onSubmit);
  const onFooterRef = useRef(onFooterStateChange);
  onSubmitRef.current = onSubmit;
  onFooterRef.current = onFooterStateChange;

  useEffect(() => {
    onFooterRef.current?.({
      canSubmit,
      isSubmitting,
      onSubmit: () => onSubmitRef.current?.({ agree1, agree2 }),
    });
  }, [agree1, agree2, canSubmit, isSubmitting]);

  const uploadEntries = Object.entries(uploads || {}).filter(([, files]) => Array.isArray(files) && files.length > 0);

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <ReviewGroup
          key={section.stepId}
          title={section.title || FORM_WIZARD_STEPS[section.stepIndex]?.label}
          stepIndex={section.stepIndex}
          onEdit={onEditStep}
        >
          {section.rows?.map((row) => (
            <ReviewRow key={`${row.label}-${row.value}`} label={row.label} value={row.value} />
          ))}
          {section.content}
        </ReviewGroup>
      ))}

      {uploadEntries.length > 0 ? (
        <ReviewGroup title="Documents" stepIndex={documentsStepIndex} onEdit={onEditStep}>
          {uploadEntries.map(([key, files]) => (
            <div key={key} className="min-w-0">
              <p className="text-slate-500 text-xs mb-1">{uploadLabels[key] || key}</p>
              <ul className="space-y-0.5">
                {files.map((f) => (
                  <li key={f.id || f.file_name || f.name} className="text-sm font-medium truncate">
                    {f.file_name || f.name || "File"}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </ReviewGroup>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-sm font-bold text-slate-900">Declarations</p>
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            checked={agree1}
            onChange={(e) => setAgree1(e.target.checked)}
          />
          <span>
            {declarations?.text1 ||
              "I confirm that the information provided is true and complete to the best of my knowledge."}
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
            checked={agree2}
            onChange={(e) => setAgree2(e.target.checked)}
          />
          <span>
            {declarations?.text2 ||
              "I authorise Dynesourcer and its partners to process this application and contact me about it."}
          </span>
        </label>
      </div>

      {!hideSubmitButton ? (
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => onSubmit?.({ agree1, agree2 })}
          className={`w-full px-6 py-3 rounded-xl text-sm font-semibold text-white ${
            canSubmit ? "bg-sky-600 hover:bg-sky-700" : "bg-sky-300 cursor-not-allowed"
          }`}
        >
          {isSubmitting ? "Submitting…" : "Submit Application"}
        </button>
      ) : null}
    </div>
  );
}

export { ReviewRow };
